import { useNavigator } from "../../lib/Hooks";
import { toSnakeCase } from "../../lib/Util";
import { hasPermission } from "../Permissions";

import { Box, Button, Grid, styled } from "@mui/material";
import Permissions from "../../constants/permissions";
import useAuth from "../../context/UserContext";
import { DefaultPanel, PanelListDetail } from "../ui/panels";
import { INTEGRATION_TYPES, IntegrationMetadata } from "./IntegrationHooks";

const ChooseTypeCard = ({
  meta,
  onSelect,
}: {
  meta: IntegrationMetadata;
  onSelect: (meta: IntegrationMetadata) => void;
}) => (
  <Grid container spacing={1} sx={{ flexDirection: "row", alignItems: "center" }} data-cy="type-card">
    <Grid size={1}>
      <Box component="img" src={meta.image} sx={{ maxHeight: "70px", maxWidth: "70px", width: "100%" }} />
    </Grid>
    <Grid size={9}>
      <h4 data-cy="card-title">{meta.title}</h4>
      {meta.description}
    </Grid>
    <Grid container size={2} sx={{ justifyContent: "flex-end" }}>
      <Button
        id={toSnakeCase(`add_${meta.integrationType}`)}
        onClick={() => onSelect(meta)}
        data-cy={toSnakeCase(`add_${meta.integrationType}`)}
      >
        Add
      </Button>
    </Grid>
  </Grid>
);

const ListWrapper = styled(Grid)({
  display: "flex",
  flexDirection: "column",
  gap: "30px",
});

interface ChooseTypeProps {
  onSelect: (meta: IntegrationMetadata) => void;
  allowHidden: boolean;
  allowTest: boolean;
}

const ChooseType = ({ onSelect, allowHidden, allowTest }: ChooseTypeProps) => (
  <ListWrapper>
    {INTEGRATION_TYPES.filter((meta) => {
      let keep = true;
      // @ts-ignore
      if (meta.hidden) keep = allowHidden;
      // @ts-ignore
      else if (meta.test) keep = allowTest;
      return keep;
    }).map((meta) => {
      return <ChooseTypeCard key={meta.integrationType} meta={meta} onSelect={onSelect} />;
    })}
  </ListWrapper>
);

const IntegrationTypeSelector = () => {
  const { user } = useAuth();
  const navigator = useNavigator();

  const onSelect = (meta: { oauth: boolean; integrationType: string }) => {
    if (meta.oauth) {
      navigator.goto("integrations", {
        mode: "connect",
        type: meta.integrationType,
      });
    } else {
      navigator.goto("integrations", {
        mode: "add",
        type: meta.integrationType,
      });
    }
  };

  const cancel = () => {
    navigator.goto("integrations");
  };

  const allowHidden = hasPermission(Permissions.HiddenIntegrations, user);
  const allowTest = hasPermission(Permissions.TestScreens, user);

  return (
    <DefaultPanel.Container id="selectIntegrationTypePanel" data-cy="integration-type-panel">
      <DefaultPanel.Header>
        Add Integration
        <PanelListDetail.PullRight>
          <Button variant="text" id="cancelAddIntegration" onClick={cancel}>
            Cancel
          </Button>
        </PanelListDetail.PullRight>
      </DefaultPanel.Header>
      <DefaultPanel.Body>
        <ChooseType onSelect={onSelect} allowHidden={allowHidden} allowTest={allowTest} />
      </DefaultPanel.Body>
    </DefaultPanel.Container>
  );
};

export { IntegrationTypeSelector };
